import { useState, useMemo, useRef, useEffect } from "react";
import { Check, ChevronsUpDown, Star, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { insertCompanyAccount } from "@/lib/companyAccountCatalog";
import { expandAccountShortcut, inferAccountLength } from "@/lib/accountShortcut";
import { useToast } from "@/hooks/use-toast";

export interface SuggestionEntry {
  account: string;
  description: string;
  matchesPlate: boolean;
}

export interface HistoryEntry {
  account: string;
  description: string;
  useCount: number;
}

export interface CatalogEntry {
  account: string;
  description: string;
}

interface Props {
  value: string;
  onChange: (account: string, description?: string) => void;
  suggestions?: SuggestionEntry[];
  history?: HistoryEntry[];
  catalog?: CatalogEntry[];
  placeholder?: string;
  disabled?: boolean;
  triggerClassName?: string;
  /** Empresa activa: si se pasa junto con onAccountCreated, se habilita el alta inline de contrapartidas. */
  companyId?: string | null;
  /** Notifica al padre que se ha creado una cuenta en company_account_catalog para que refresque su catálogo. */
  onAccountCreated?: (account: string, description: string) => void;
}

const ExpenseAccountCombobox = ({
  value,
  onChange,
  suggestions = [],
  history = [],
  catalog = [],
  placeholder = "Cuenta de gasto",
  disabled,
  triggerClassName,
  companyId,
  onAccountCreated,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { toast } = useToast();

  /** Estado del mini-formulario inline para crear nueva cuenta de contrapartida. */
  const [createForm, setCreateForm] = useState<{ account: string; description: string } | null>(null);
  const [creating, setCreating] = useState(false);

  const canCreate = !!companyId && !!onAccountCreated;


  // Deduplicate sets across the three groups
  const { groupedSuggestions, groupedHistory, groupedCatalog } = useMemo(() => {
    const seen = new Set<string>();
    const sug = suggestions.filter((s) => {
      if (!s.account || seen.has(s.account)) return false;
      seen.add(s.account);
      return true;
    });
    // sort: matches plate first
    sug.sort((a, b) => Number(b.matchesPlate) - Number(a.matchesPlate));

    const hist = history.filter((h) => {
      if (!h.account || seen.has(h.account)) return false;
      seen.add(h.account);
      return true;
    });

    const cat = catalog.filter((c) => {
      if (!c.account || seen.has(c.account)) return false;
      seen.add(c.account);
      return true;
    });

    return { groupedSuggestions: sug, groupedHistory: hist, groupedCatalog: cat };
  }, [suggestions, history, catalog]);

  const trimmed = search.trim();
  const hasAnyMatch = useMemo(() => {
    if (!trimmed) return true;
    const q = trimmed.toLowerCase();
    const all = [...groupedSuggestions, ...groupedHistory, ...groupedCatalog];
    return all.some(
      (e) =>
        e.account.toLowerCase().includes(q) ||
        (e.description || "").toLowerCase().includes(q),
    );
  }, [trimmed, groupedSuggestions, groupedHistory, groupedCatalog]);

  const handleSelect = (account: string, description?: string) => {
    setOpen(false);
    setSearch("");
    // Defer state update to avoid Radix focus race conditions
    setTimeout(() => {
      onChange(account, description);
    }, 0);
  };

  // Reset search and create form when closing
  useEffect(() => {
    if (!open) {
      setSearch("");
      setCreateForm(null);
      setCreating(false);
    }
  }, [open]);

  const startCreate = (initialAccount = "") => {
    setCreateForm({ account: initialAccount.trim(), description: "" });
  };

  const accountLength = useMemo(() => inferAccountLength(catalog.map((c) => c.account)), [catalog]);

  const saveNewAccount = async () => {
    if (!createForm || !companyId || !onAccountCreated) return;
    const account = expandAccountShortcut(createForm.account.trim(), accountLength);
    const description = createForm.description.trim();
    if (!account) {
      toast({
        title: "Falta el número de cuenta",
        description: "Introduce el código antes de guardar.",
        variant: "destructive",
      });
      return;
    }
    // Si ya existe en el catálogo cargado, no insertar: simplemente seleccionar.
    const existing = catalog.find((c) => c.account.trim() === account);
    if (existing) {
      handleSelect(existing.account, existing.description);
      return;
    }
    setCreating(true);
    try {
      const { data, error } = await insertCompanyAccount(companyId, account, description);
      if (error || !data) {
        const isPerms = /permission|row-level security/i.test(error || "");
        toast({
          title: isPerms
            ? "Solo los administradores pueden crear cuentas"
            : "No se pudo crear la cuenta",
          description: isPerms
            ? "Pide a un administrador que la añada al catálogo."
            : error || "Error desconocido",
          variant: "destructive",
        });
        setCreating(false);
        return;
      }
      onAccountCreated(data.account, data.description);
      toast({
        title: "Cuenta creada",
        description: `${data.account}${data.description ? " · " + data.description : ""}`,
      });
      handleSelect(data.account, data.description);
    } catch (e) {
      console.error("insertCompanyAccount error:", e);
      toast({
        title: "Error al crear la cuenta",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-6 px-1.5 py-0 font-mono text-xs justify-between gap-1 hover:bg-accent w-full",
            !value && "text-muted-foreground",
            triggerClassName,
          )}
        >
          {value ? (
            <span className="truncate">
              <span>{value}</span>
              {(() => {
                const desc =
                  catalog.find((c) => c.account === value)?.description ||
                  suggestions.find((s) => s.account === value)?.description ||
                  history.find((h) => h.account === value)?.description ||
                  "";
                return desc ? <span className="text-muted-foreground"> — {desc}</span> : null;
              })()}
            </span>
          ) : (
            <span className="truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[28rem] p-0" align="start">
        {createForm ? (
          <div className="space-y-3 p-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Nueva cuenta de contrapartida
              </h4>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setCreateForm(null)}
                disabled={creating}
                title="Cancelar"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-account" className="text-[10px] uppercase text-muted-foreground">
                Cuenta
              </Label>
              <Input
                id="new-account"
                value={createForm.account}
                onChange={(e) =>
                  setCreateForm((f) => (f ? { ...f, account: e.target.value.replace(/[^\d.]/g, "") } : f))
                }
                onBlur={(e) =>
                  setCreateForm((f) => (f ? { ...f, account: expandAccountShortcut(e.target.value, accountLength) } : f))
                }
                placeholder="62900000 (atajo: 629.5)"
                className="h-8 font-mono text-xs"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-account-desc" className="text-[10px] uppercase text-muted-foreground">
                Descripción
              </Label>
              <Input
                id="new-account-desc"
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm((f) => (f ? { ...f, description: e.target.value } : f))
                }
                placeholder="Concepto"
                className="h-8 text-xs"
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void saveNewAccount();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateForm(null)}
                disabled={creating}
              >
                Cancelar
              </Button>
              <Button size="sm" onClick={() => void saveNewAccount()} disabled={creating}>
                {creating ? "Guardando…" : "Guardar y aplicar"}
              </Button>
            </div>
          </div>
        ) : (
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Filtrar por nº de cuenta o descripción..."
            value={search}
            onValueChange={setSearch}
            className="h-9 text-xs"
          />
          <div className="px-3 py-1 border-b text-[10px] text-muted-foreground flex justify-between">
            <span>Sugeridas: {groupedSuggestions.length} · Histórico: {groupedHistory.length} · Catálogo: {groupedCatalog.length}</span>
            <span>Total: {groupedSuggestions.length + groupedHistory.length + groupedCatalog.length}</span>
          </div>
          {canCreate && (
            <div className="border-b p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-full justify-start gap-2 text-xs text-primary"
                onClick={() => startCreate(trimmed)}
              >
                <Plus className="h-3 w-3" />
                Crear nueva cuenta de contrapartida…
                {trimmed && <span className="font-mono font-semibold">{trimmed}</span>}
              </Button>
            </div>
          )}
          <CommandList className="max-h-[28rem]">
            {groupedSuggestions.length > 0 && (
              <CommandGroup heading="Sugeridas (maestro)">
                {groupedSuggestions.map((s) => (
                  <CommandItem
                    key={`sug-${s.account}`}
                    value={`${s.account} ${s.description}`}
                    onSelect={() => handleSelect(s.account, s.description)}
                    className="text-xs"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3 w-3",
                        value === s.account ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex-1 min-w-0 flex items-center gap-1.5">
                      <span className="font-mono">{s.account}</span>
                      {s.description && (
                        <span className="truncate text-muted-foreground">— {s.description}</span>
                      )}
                    </div>
                    {s.matchesPlate && (
                      <Badge
                        variant="default"
                        className="ml-1 gap-0.5 px-1 py-0 text-[9px] bg-[hsl(var(--brand-green))] text-white hover:bg-[hsl(var(--brand-green))]"
                      >
                        <Star className="h-2.5 w-2.5" />
                        Matrícula
                      </Badge>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {groupedHistory.length > 0 && (
              <>
                {groupedSuggestions.length > 0 && <CommandSeparator />}
                <CommandGroup heading="Histórico para este proveedor">
                  {groupedHistory.map((h) => (
                    <CommandItem
                      key={`hist-${h.account}`}
                      value={`${h.account} ${h.description}`}
                      onSelect={() => handleSelect(h.account, h.description)}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-3 w-3",
                          value === h.account ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <div className="flex-1 min-w-0 flex items-center gap-1.5">
                        <span className="font-mono">{h.account}</span>
                        {h.description && (
                          <span className="truncate text-muted-foreground">— {h.description}</span>
                        )}
                      </div>
                      <span className="ml-1 text-[10px] text-muted-foreground">×{h.useCount}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {groupedCatalog.length > 0 && (
              <>
                {(groupedSuggestions.length > 0 || groupedHistory.length > 0) && <CommandSeparator />}
                <CommandGroup heading="Todas las cuentas">
                  {groupedCatalog.map((c) => (
                    <CommandItem
                      key={`cat-${c.account}`}
                      value={`${c.account} ${c.description}`}
                      onSelect={() => handleSelect(c.account, c.description)}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-3 w-3",
                          value === c.account ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <div className="flex-1 min-w-0 flex items-center gap-1.5">
                        <span className="font-mono">{c.account}</span>
                        {c.description && (
                          <span className="truncate text-muted-foreground">— {c.description}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {!hasAnyMatch && trimmed && (
              <CommandGroup heading="Nueva cuenta">
                <CommandItem
                  value={`__new__ ${trimmed}`}
                  onSelect={() => handleSelect(trimmed)}
                  className="text-xs"
                >
                  <Check className="mr-2 h-3 w-3 opacity-0" />
                  Usar <span className="mx-1 font-mono font-semibold">{trimmed}</span> sin guardar
                </CommandItem>
                {canCreate && (
                  <CommandItem
                    value={`__create__ ${trimmed}`}
                    onSelect={() => startCreate(trimmed)}
                    className="text-xs"
                  >
                    <Plus className="mr-2 h-3 w-3" />
                    Crear <span className="mx-1 font-mono font-semibold">{trimmed}</span> en el catálogo
                  </CommandItem>
                )}
              </CommandGroup>
            )}

            {hasAnyMatch &&
              groupedSuggestions.length === 0 &&
              groupedHistory.length === 0 &&
              groupedCatalog.length === 0 && (
                <CommandEmpty className="text-xs py-4">
                  No hay cuentas disponibles. Escribe para crear una.
                </CommandEmpty>
              )}

          </CommandList>
        </Command>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ExpenseAccountCombobox;
